import { createEffect, createSignal, onCleanup, For, onMount, createMemo, batch } from "solid-js";
import { View, Text, hexColor, Show, ElementNode } from "@lightningtv/solid";
import { useNavigate, useParams } from "@solidjs/router";
import styles from "../../styles";
import {
  playListPassword,
  playListUrl,
  playListUsername,
  setGlobalBackground,
} from "../../../state";
import { Row } from "@lightningtv/solid-ui";
import { LazyUp } from "@lightningtv/solid/primitives";
import LoadingIndicator from "../loading/loading";
import { fetchData } from "../../api/workers/fetchWorkerManager";
import Header from "../../components/Header";
import { fontFamily, isRTL, t } from "../../translations/configuration";

const layout = [
  ["A", "B", "C", "D", "E", "F"],     // 0
  ["G", "H", "I", "J", "K", "L"],     // 1
  ["M", "N", "O", "P", "Q", "R"],     // 2
  ["S", "T", "U", "V", "W", "X"],     // 3
  ["Y", "Z", "1", "2", "3", "4"],     // 4
  ["5", "6", "7", "8", "9", "0"]      // 5
];

// Simple search function - no complex indexing
const simpleSearch = (query: string, data: any[]) => {
  if (!query.trim()) return data;

  const queryLower = query.toLowerCase().trim();
  return data.filter((item: any) =>
    item.name?.toLowerCase().includes(queryLower)
  );
};

const SearchPage = () => {
  const params = useParams();
  const navigate = useNavigate();

  const refs: Array<Array<any>> = layout.map(() => []);
  const [inputValue, setInputValue] = createSignal("");
  const [isLoading, setIsLoading] = createSignal(true);
  const [hasError, setHasError] = createSignal(false);

  let lastFocusedKey = refs[0][0];
  let spaceRef: any, backspaceRef: any, searchRef: any, viewRef: any, headerRef: any;

  // Simplified data management
  const [allMovies, setAllMovies] = createSignal([]);
  const [displayedItems, setDisplayedItems] = createSignal([]);

  // Debounced search
  let searchTimeout: ReturnType<typeof setTimeout> | null = null;

  // Simple data fetching
  const fetchMoviesData = async () => {
    console.log("🔄 Starting movie data fetch");
    setIsLoading(true);
    setHasError(false);

    const username = playListUsername();
    const password = playListPassword();
    const name = playListUrl();
    const url = `${name}/player_api.php?username=${username}&password=${password}&action=get_vod_streams`;

    try {
      const result = await fetchData(url);

      if (Array.isArray(result) && result.length > 0) {
        // console.log("✅ Movies loaded:", result);

        // Set data immediately
        setAllMovies(result);
        setDisplayedItems(result); // Show all movies initially
        setIsLoading(false);

        // Focus on first key
        setTimeout(() => {
          refs[0][0]?.setFocus?.();
        }, 100);
      } else {
        throw new Error("Invalid data");
      }
    } catch (error) {
      console.error("❌ Error fetching movies:", error);
      setIsLoading(false);
      setHasError(true);
    }
  };

  // Simple search function
  const performSearch = (query: string) => {
    const movies = allMovies();
    if (!movies.length) return;

    const results = simpleSearch(query, movies);
    console.log(`🔍 Search "${query}": ${results.length} results`);
    setDisplayedItems(results);
  };

  // Debounced search effect
  createEffect(() => {
    const query = inputValue();

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    searchTimeout = setTimeout(() => {
      performSearch(query);
    }, 300);
  });

  // Initialize component
  onMount(() => {
    console.log("🚀 Search component mounted");
    setGlobalBackground("#0A1C34");

    // Always start with empty search input
    setInputValue("");

    // Start data fetching immediately
    fetchMoviesData();
  });

  // Remove the persist search input effect entirely
  // createEffect(() => {
  //   localStorage.setItem("searchInput", inputValue());
  // });

  // Cleanup
  onCleanup(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
  });

  // Helper function to handle keypad interactions
  const handleKeypadInteraction = (callback: () => void) => {
    if (!isLoading() && !hasError()) {
      callback();
    }
  };

  // Input handling
  const addCharacter = (char: string) => {
    setInputValue(prev => prev + char);
  };

  const removeCharacter = () => {
    setInputValue(prev => prev.slice(0, -1));
  };

  const addSpace = () => {
    setInputValue(prev => prev + " ");
  };

  return (
    <View color={hexColor("#182027")}>
      <View
        width={1920}
        onEscape={() => {
          if (!isLoading()) {
            navigate(-1);
          }
        }}
        height={1700}
        positionAbsolute={0}
        src={"https://i.ibb.co/Lzk5hD4Y/Maskgroup.png"}
      >
        <Header screen={t("search")} ref={(el) => headerRef = el} onDown={() => searchRef?.setFocus()} />

        <View x={isRTL() ? 50 : 1600} y={50}>
          <View
            onEnter={() => {
              handleKeypadInteraction(() => navigate("/userInfo"));
            }}
            onUp={() => headerRef?.setFocus()}
            onDown={() => {
              handleKeypadInteraction(() => backspaceRef?.setFocus());
            }}
            ref={searchRef}
            x={120}
            style={{
              width: 100,
              height: 100,
              opacity: isLoading() || hasError() ? 0.3 : 1
            }}
          //style={styles.searchButton}
          >
            <View x={28} y={28} src="https://i.ibb.co/Jjp8bmn4/setting.png" width={42} height={42} />
          </View>
        </View>

        {/* Single Loading Overlay */}
        <Show when={isLoading()}>
          <View
            width={1920}
            height={1700}
            color={hexColor("#000000")}
            alpha={0.7}
            positionAbsolute={0}
            style={{ zIndex: 1000 }}
          >
            <View x={960} y={500}>
              <LoadingIndicator isCategoriesIndicator={false} />
            </View>
          </View>
        </Show>

        {/* Error Overlay */}
        <Show when={hasError()}>
          <View
            width={1920}
            height={1700}
            color={hexColor("#000000")}
            alpha={0.8}
            positionAbsolute={0}
            style={{ zIndex: 1000 }}
          >
            <View x={1160} y={300} style={{ textAlign: 'center' }}>
              <View x={-50} y={0} width={100} height={100}>
                <Text fontSize={80} color={hexColor("#FF6B6B")}>⚠️</Text>
              </View>
              <Text
                x={-300}
                y={120}
                fontSize={42}
                color={hexColor("#FFFFFF")}
                fontWeight="bold"
                style={{ textAlign: 'center', fontFamily: fontFamily() }}
              >
                {t("thereIsProblem")}
              </Text>
              <Text
                x={-150}
                y={180}
                fontSize={32}
                color={hexColor("#CCCCCC")}
                style={{ textAlign: 'center', fontFamily: fontFamily() }}
              >
                {t("tryAgainLater")}
              </Text>
            </View>
          </View>
        </Show>

        <View style={{ opacity: isLoading() || hasError() ? 0.3 : 1 }}>
          {/* Input Text Display */}
          <View x={100} y={220} style={styles.containerText}>
            <Text fontSize={48} x={20} y={15}>{inputValue()}</Text>
          </View>

          {/* Space Key */}
          <View
            ref={(el) => (spaceRef = el)}
            x={100}
            y={310}
            style={styles.space}
            onEnter={() => handleKeypadInteraction(addSpace)}
            onDown={() => handleKeypadInteraction(() => refs[0][0]?.setFocus())}
            onRight={() => handleKeypadInteraction(() => backspaceRef?.setFocus())}
            onUp={() => handleKeypadInteraction(() => searchRef?.setFocus())}
          >
            <View src={"https://i.ibb.co/G4SDmcq9/space.png"} width={100} height={80} x={80} />
          </View>

          {/* Backspace Key */}
          <View
            ref={(el) => (backspaceRef = el)}
            x={360}
            y={310}
            style={styles.space}
            onEnter={() => handleKeypadInteraction(removeCharacter)}
            onDown={() => handleKeypadInteraction(() => refs[0][0])}
            onLeft={() => handleKeypadInteraction(() => spaceRef?.setFocus())}
            onUp={() => handleKeypadInteraction(() => searchRef?.setFocus())}
          >
            <View src={"https://i.ibb.co/wh6n5N3X/backspace.png"} width={70} height={60} y={10} x={100} />
          </View>

          {/* Character Keys */}
          <View x={100} y={400}>
            {layout.map((row, rowIndex) => (
              <View y={rowIndex * 85}>
                {row.map((char, colIndex) => (
                  <View
                    ref={(el) => {
                      refs[rowIndex][colIndex] = el;
                      el.onFocus = () => {
                        if (!isLoading() && !hasError()) {
                          lastFocusedKey = el;
                        }
                      };
                    }}
                    style={styles.buttonKey}
                    x={colIndex * 85}
                    onEnter={() => handleKeypadInteraction(() => addCharacter(char))}
                    onRight={() => {
                      handleKeypadInteraction(() => {
                        const next = refs[rowIndex][colIndex + 1];
                        if (next) {
                          next?.setFocus();
                        } else {
                          viewRef?.setFocus();
                        }
                      });
                    }}
                    onLeft={() => {
                      handleKeypadInteraction(() => {
                        const prev = refs[rowIndex][colIndex - 1];
                        if (prev) prev?.setFocus();
                      });
                    }}
                    onDown={() => {
                      handleKeypadInteraction(() => {
                        const down = refs[rowIndex + 1]?.[colIndex];
                        if (down) down?.setFocus();
                      });
                    }}
                    onUp={() => {
                      handleKeypadInteraction(() => {
                        if (rowIndex === 0) {
                          spaceRef?.setFocus();
                        } else {
                          const up = refs[rowIndex - 1]?.[colIndex];
                          if (up) up?.setFocus();
                        }
                      });
                    }}
                  >
                    <Text fontSize={40} x={25} y={25}>{char}</Text>
                  </View>
                ))}
              </View>
            ))}
          </View>
        </View>

        <Text fontSize={38} x={750} y={230} style={{ fontFamily: fontFamily() }}>{t("movies")}</Text>

        {/* Movies Display */}
        <View y={300} x={750} clipping gap={20}>
          <Show
            when={!isLoading() && !hasError()}
            fallback={
              <Show when={isLoading()}>
                <View x={1350} y={100}>
                  <LoadingIndicator isCategoriesIndicator={false} />
                </View>
              </Show>
            }
          >
            <Show when={displayedItems().length === 0}>
              <Text x={1350} fontSize={30} style={{ fontFamily: fontFamily() }}>{t("noMoviesFound")}</Text>
            </Show>

            <Show when={displayedItems().length > 0}>
              <LazyUp
                component={Row}
                onLeft={() => handleKeypadInteraction(() => lastFocusedKey?.setFocus())}
                ref={(el) => (viewRef = el)}
                scroll="always"
                direction="row"
                upCount={8}
                each={displayedItems()}
              >
                {(item: any) => {
                  return (
                    <View
                      onEnter={() => {
                        handleKeypadInteraction(() => {
                          console.log("Navigating to:", item());
                          navigate(`/entity/movie/${item().stream_id}/${item().category_id}`);
                        });
                      }}
                      x={950}
                      style={styles.moviesBackgroundItem}
                    >
                      <View
                        x={7}
                        y={7}
                        borderRadius={10}
                        width={244}
                        height={390}
                        src={item().stream_icon}
                      />
                    </View>
                  );
                }}
              </LazyUp>
            </Show>
          </Show>
        </View>
      </View>
    </View>
  );
};

export default SearchPage;